import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Typography } from '@/components/ui/typography'
import { useAppForm } from '@/lib/form'
import { getOrderQuoteLockState } from '@/lib/order-quote'
import { useSavePosCartMutation, useUnsavePosCartMutation } from '@/lib/services/carts/carts.query'
import { savePosCartSchema } from '@/lib/services/carts/carts.schema'
import { getErrorMessage } from '@/lib/utils'
import { Bookmark, BookmarkX } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

const SAVED_LABEL_MAX_LENGTH = 120

export function SavedPosCartSummary({ cart }) {
  // Once it becomes an order it is no longer a draft, whatever the marker says.
  if (!cart?.saved_at || cart.sales_order_id) {
    return null
  }

  const summary = [
    cart.saved_by?.name ? `Draft saved by ${cart.saved_by.name}` : 'Draft',
    cart.saved_label,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <Typography variant="label" tone="muted" className="block truncate">
      {summary}
    </Typography>
  )
}

function UnsavePosCartButton({ cart, disabled }) {
  const unsavePosCartMutation = useUnsavePosCartMutation()

  const handleUnsave = async () => {
    try {
      await unsavePosCartMutation.mutateAsync(cart.id)
      toast.success('Removed from Draft Orders')
    } catch (error) {
      toast.error("We couldn't remove this order from Draft Orders", {
        description: getErrorMessage(error) || 'Please try again.',
      })
    }
  }

  return (
    <Button
      variant="outline"
      size="sm"
      disabled={disabled || unsavePosCartMutation.isPending}
      onClick={handleUnsave}
    >
      <BookmarkX />
      Remove from Drafts
    </Button>
  )
}

export function SavePosCartDialog({ cart, disabled = false, onSaved }) {
  const [open, setOpen] = useState(false)
  const lockState = getOrderQuoteLockState(cart)

  // An existing draft offers the reverse action instead of saving again. One holding a
  // payment stays in Draft Orders until that payment is resolved.
  if (cart?.saved_at) {
    return lockState.canUnsave ? <UnsavePosCartButton cart={cart} disabled={disabled} /> : null
  }

  // The server owns this rule: unconverted POS drafts with items, or holding a payment.
  if (!lockState.canSaveForLater) {
    return null
  }

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        disabled={disabled || !cart}
        onClick={() => setOpen(true)}
      >
        <Bookmark />
        Save as Draft
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent size="compact">
          <DialogHeader>
            <DialogTitle>Save as draft</DialogTitle>
            <DialogDescription>
              {lockState.holdsCustomerMoney
                ? 'This order has a payment attached. Anyone on your team can pick it back up from Draft Orders to finish it or return the payment.'
                : "Anyone on your team can pick this order back up from Draft Orders. Prices update when it's reopened."}
            </DialogDescription>
          </DialogHeader>
          {/* Mounted only while open so each open starts with an empty label. */}
          {open ? (
            <SavePosCartForm
              cart={cart}
              onCancel={() => setOpen(false)}
              onSaved={() => {
                setOpen(false)
                onSaved?.()
              }}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  )
}

function SavePosCartForm({ cart, onCancel, onSaved }) {
  const savePosCartMutation = useSavePosCartMutation()
  const form = useAppForm({
    defaultValues: { label: '' },
    validators: {
      onSubmit: savePosCartSchema,
      onSubmitAsync: async ({ value }) => {
        try {
          await savePosCartMutation.mutateAsync({
            cartId: cart.id,
            label: value.label,
          })
          toast.success('Draft saved', {
            description: 'Find it anytime under Draft Orders.',
          })
          onSaved()
        } catch (error) {
          toast.error("We couldn't save this order", {
            description: getErrorMessage(error) || 'Please try again.',
          })
          if (error?.response?.data?.errors) {
            return { fields: error.response.data.errors }
          }
        }
      },
    },
  })

  return (
    <form.AppForm>
      <form.Form>
        <form.AppField name="label">
          {(field) => (
            <field.TextInput
              id="saved-pos-cart-label"
              label="Label (Optional)"
              placeholder="e.g. Mrs. Lee – calling back about colors"
              maxLength={SAVED_LABEL_MAX_LENGTH}
              characterMaxLength={SAVED_LABEL_MAX_LENGTH}
              autoFocus
            />
          )}
        </form.AppField>

        <DialogFooter className="mt-6">
          <Button variant="outline" type="button" onClick={onCancel}>
            Cancel
          </Button>
          <form.SubmitButton className="w-auto" mutation={savePosCartMutation}>
            Save Draft
          </form.SubmitButton>
        </DialogFooter>
      </form.Form>
    </form.AppForm>
  )
}
